import { AzureSearchBarAutocomplete } from '@/components/AzureSearchBarAutocomplete'
import Image from '@/components/Image'
import RichText from '@/components/RichText'
import SanitisedHTMLContainer from '@/components/SanitisedHTMLContainer'
import { EMPTY_KONTENT_RICHTEXT } from '@/constants'
import type { Contentblock } from '@/kontent/content-types/contentblock'
import type { UiHerobanner } from '@/kontent/content-types/ui_herobanner'
import type { Weblinkext } from '@/kontent/content-types/weblinkext'
import VideoModal from '@/legacy-ported/components/base/VideoModal'
import { getLinkFromLinkUI } from '@/utils/getLinkFromLinkUI'
import { HeroBanner } from 'nsw-ds-react'
import { Fragment, useState } from 'react'
import type { RichtextSectionProps } from '.'
import {
	getLinkedItems,
	useKontentHomeConfig,
} from '../contexts/KontentHomeConfigProvider'

export const HomepageBanner = (props: RichtextSectionProps<UiHerobanner>) => {
	const [openVideoModal, setOpenVideoModal] = useState(false)
	const { linkedItem, mappings } = props
	const { pageResponseLinkedItems } = useKontentHomeConfig()

	const handleOnClick = (e) => {
		e.preventDefault()
		setOpenVideoModal(true)
	}

	const handleVideoModalCancel = () => {
		setOpenVideoModal(false)
	}

	const displayMode =
		linkedItem.elements.display?.value?.[0]?.codename || 'supporting_image'
	const themeCodename =
		linkedItem.elements.theme?.value?.[0]?.codename || 'brand_dark'
	// 'off-white' is supported by the NSW design system SCSS
	// (.nsw-hero-banner--off-white) but not exposed in nsw-ds-react's `style`
	// prop type. We pass `style="white"` to the lib (closest match: dark text)
	// and append the .nsw-hero-banner--off-white class via `className` to apply
	// the off-white background.
	const style: 'dark' | 'light' | 'white' | 'off-white' =
		themeCodename === 'brand_light'
			? 'light'
			: themeCodename === 'white'
			? 'white'
			: themeCodename === 'off_white'
			? 'off-white'
			: 'dark'

	// nsw-ds-react accepts only 'dark' | 'light' | 'white' for the style prop.
	// For our 'off-white' case, pass 'white' as the base and add the off-white
	// modifier class manually.
	const heroBannerStyle: 'dark' | 'light' | 'white' =
		style === 'off-white' ? 'white' : style

	return (
		<section data-kontent-item-id={linkedItem.system.id} className="module">
			{getLinkedItems(
				linkedItem.elements.items,
				pageResponseLinkedItems,
			).map((item: Contentblock) => {
				const moreInfoLinkItems = getLinkedItems(
					item.elements.more_info_link,
					pageResponseLinkedItems,
				) as Weblinkext[]

				const primaryLink = moreInfoLinkItems?.[0]
				let url = ''
				if (primaryLink) {
					const { url: _url } = getLinkFromLinkUI(
						primaryLink,
						mappings,
						pageResponseLinkedItems,
					)
					url = _url
				}

				const imageAsset = item.elements.image.value?.[0]

				const intro = (
					<SanitisedHTMLContainer>
						{item.elements.sub_title.value}
					</SanitisedHTMLContainer>
				)

				const imageNode = imageAsset ? (
					<div className="w-full h-full relative">
						<Image
							alt={imageAsset.description || ''}
							asset={imageAsset}
							layout="fill"
							priority
							objectFit="cover"
						/>
					</div>
				) : undefined

				const cta =
					url && primaryLink?.elements.title.value
						? {
								text: primaryLink.elements.title.value,
								url,
								onClick: handleOnClick,
						  }
						: undefined

				if (displayMode === 'hero_search') {
					const imageUrl = imageAsset?.url || ''
					const wrapperBgColor =
						style === 'dark'
							? 'var(--nsw-brand-dark)'
							: style === 'light'
							? 'var(--nsw-brand-light)'
							: style === 'off-white'
							? 'var(--nsw-off-white)'
							: 'var(--nsw-white)'
					const wrapperTextColor =
						style === 'dark'
							? 'var(--nsw-text-light)'
							: 'var(--nsw-text-dark)'
					return (
						<Fragment key={item.system.id}>
							<div
								className="nsw-section nsw-section--image"
								style={
									imageUrl
										? {
												backgroundImage: `url(${imageUrl})`,
										  }
										: undefined
								}
							>
								<div className="hero-search">
									<div className="nsw-container">
										<div
											className="hero-search__wrapper"
											style={{
												backgroundColor: wrapperBgColor,
												color: wrapperTextColor,
											}}
											css={{
												// Force all link states (default/hover/visited/focus)
												// to match the theme text color — overrides browser purple visited.
												'a, a:hover, a:visited, a:focus':
													{
														color:
															style === 'dark'
																? 'var(--nsw-text-light)'
																: 'var(--nsw-brand-dark)',
													},
											}}
										>
											<div className="hero-search__content">
												<h1>
													{item.elements.title.value}
												</h1>
												{item.elements.sub_title
													.value && (
													<p className="nsw-intro">
														<SanitisedHTMLContainer>
															{
																item.elements
																	.sub_title
																	.value
															}
														</SanitisedHTMLContainer>
													</p>
												)}
												<div
													className="hero-search__form"
													css={{
														// Per NSW design system hero-search:
														// input is always white, button is dark blue
														'.nsw-form__input': {
															backgroundColor:
																'var(--nsw-white)',
															color: 'var(--nsw-text-dark)',
														},
														'.nsw-form__input:focus, .nsw-form__input.Mui-focused':
															{
																backgroundColor:
																	'var(--nsw-white)',
															},
													}}
												>
													<AzureSearchBarAutocomplete
														initialSearchText=""
														placeholder="Search"
														handleSearchPress={(
															value,
														) => {
															if (
																typeof value ===
																'string'
															) {
																window.location.href = `/search?q=${value}`
															}
														}}
														suggestionNumber={10}
														disableNextRouteRedirect
														hideClearButton
														searchButtonStyle={
															style === 'dark'
																? 'white'
																: 'dark'
														}
														searchIconSize={36}
														clickingItemTrackEventName="Click Hero Search Suggestion"
														searchingTrackEventName="Hero Search"
													/>
												</div>
												{(() => {
													const contentLinks =
														getLinkedItems(
															item.elements
																.content_links,
															pageResponseLinkedItems,
														)
													if (!contentLinks?.length)
														return null
													return (
														<div className="hero-search__suggested">
															<ul>
																{contentLinks.map(
																	(link) => {
																		const {
																			url,
																			isExternal,
																		} = getLinkFromLinkUI(
																			link,
																			mappings,
																			pageResponseLinkedItems,
																		)
																		// Most link types have title directly;
																		// LinkResourceFile points to a ResourceContainer
																		let title =
																			(
																				link as any
																			)
																				?.elements
																				?.title
																				?.value ||
																			''
																		if (
																			!title &&
																			link
																				.system
																				.type ===
																				'link_resource_file'
																		) {
																			const containerCodename =
																				(
																					link as any
																				)
																					.elements
																					?.item
																					?.value?.[0]
																			if (
																				containerCodename
																			) {
																				title =
																					(
																						pageResponseLinkedItems?.[
																							containerCodename
																						] as any
																					)
																						?.elements
																						?.title
																						?.value ||
																					''
																			}
																		}
																		return (
																			<li
																				key={
																					link
																						.system
																						.id
																				}
																			>
																				{isExternal ? (
																					<a
																						href={
																							url
																						}
																						target="_blank"
																						rel="nofollow noopener noreferrer"
																					>
																						{
																							title
																						}
																					</a>
																				) : (
																					<a
																						href={
																							url
																						}
																					>
																						{
																							title
																						}
																					</a>
																				)}
																			</li>
																		)
																	},
																)}
															</ul>
														</div>
													)
												})()}
											</div>
										</div>
									</div>
								</div>
							</div>
						</Fragment>
					)
				}

				const heroTextColor =
					style === 'dark'
						? 'var(--nsw-text-light)'
						: 'var(--nsw-text-dark)'

				return (
					<Fragment key={item.system.id}>
						<div
							css={{
								'.nsw-hero-banner__content, .nsw-hero-banner__links':
									{
										color: heroTextColor,
									},
							}}
						>
							<HeroBanner
								title={item.elements.title.value}
								intro={intro}
								style={heroBannerStyle}
								className={
									style === 'off-white'
										? 'nsw-hero-banner--off-white'
										: undefined
								}
								{...(displayMode === 'primary_message' ||
								displayMode === 'supporting_line_system'
									? { wide: true }
									: {})}
								{...(displayMode === 'supporting_line_system'
									? {
											className:
												style === 'off-white'
													? 'nsw-hero-banner--lines nsw-hero-banner--off-white'
													: 'nsw-hero-banner--lines',
											image: (
												<div className="nsw-hero-banner__lines" />
											),
									  }
									: {})}
								{...(displayMode === 'supporting_image'
									? { image: imageNode }
									: {})}
								{...(displayMode !== 'supporting_content' && cta
									? { cta }
									: {})}
							>
								{displayMode === 'supporting_content' &&
									item.elements.content.value &&
									item.elements.content.value !==
										EMPTY_KONTENT_RICHTEXT && (
										<div
											className="nsw-hero-banner__links"
											css={{
												// Force all link states (default,
												// hover, visited, focus) to use the
												// theme's link color so :visited
												// doesn't render as browser purple.
												'a, a:hover, a:visited, a:focus':
													{
														color:
															style === 'dark'
																? 'var(--nsw-text-light)'
																: 'var(--nsw-brand-dark)',
													},
											}}
										>
											<div className="nsw-hero-banner__list">
												<RichText
													richTextElement={
														item.elements.content
													}
													linkedItems={
														pageResponseLinkedItems
													}
													mappings={mappings}
												/>
											</div>
										</div>
									)}
							</HeroBanner>
						</div>
						{openVideoModal && url && (
							<VideoModal
								ariaLabel={primaryLink?.elements.title.value}
								modalStatus={openVideoModal}
								onCancel={handleVideoModalCancel}
								video={url}
							/>
						)}
					</Fragment>
				)
			})}
		</section>
	)
}

export default HomepageBanner
